/* eslint-disable react/no-unescaped-entities */
import React, { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { api, apiError, saveJwt } from "@/lib/api";
import { toast } from "sonner";
import ProntoLogo from "@/components/ProntoLogo";
import GoogleHandoff from "@/components/GoogleHandoff";
import { nativeOpenOAuth, nativeGoogleSignIn, remoteLog, isNative } from "@/lib/native";
// iter v29 r202 — Sign in with Apple rimosso temporaneamente per sbloccare
// TestFlight (capability sul portal era OK ma il flow runtime dava sempre
// errore). VA RIMESSO prima della submission App Store pubblica perché
// Apple Guideline 4.8 lo richiede se l'app offre altri social login.
import AppleSignInButton from "@/components/AppleSignInButton";
import { ArrowRight, GoogleLogo, ShieldCheck, Eye, EyeSlash, MagicWand, EnvelopeSimple } from "@phosphor-icons/react";

// iter v32 r12 — Rimosso _isIOSBrowser helper: il banner Safari warning
// (vedi sotto) è stato eliminato per essere meno invasivo. Il flow Google
// gestisce già internamente gli errori OAuth e mostra una toast chiara;
// chi ha problemi può usare "Accedi senza password" dal link sotto.

export default function Login() {
  const { loginJwt, setUser } = useAuth();
  const nav = useNavigate();
  const [searchParams] = useSearchParams();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  // iter v31 r19 — Magic-link state: "form" = login normale, "magic" = chiedi email
  // per ricevere link senza password, "magic-sent" = email inviata
  const [magicMode, setMagicMode] = useState(false);
  const [magicEmail, setMagicEmail] = useState("");
  const [magicSent, setMagicSent] = useState(false);
  const [magicLoading, setMagicLoading] = useState(false);

  // iter v29 r196 — Se l'utente torna sull'app dopo aver aperto Safari
  // (es. ha annullato il login Google o ha chiuso la Safari View),
  // resetta lo stato loading per non lasciare il GoogleHandoff "appeso".
  useEffect(() => {
    if (!googleLoading) return;
    const onVisible = () => {
      if (document.visibilityState === "visible") {
        setTimeout(() => setGoogleLoading(false), 500);
      }
    };
    document.addEventListener("visibilitychange", onVisible);
    const t = setTimeout(() => setGoogleLoading(false), 60000);
    return () => {
      document.removeEventListener("visibilitychange", onVisible);
      clearTimeout(t);
    };
  }, [googleLoading]);

  // iter 252: se il response interceptor ha redirezionato con ?expired=1,
  // mostriamo un toast esplicativo invece di lasciare l'utente perplesso.
  useEffect(() => {
    if (searchParams.get("expired") === "1") {
      toast.info("La tua sessione è scaduta. Accedi di nuovo per continuare.");
    }
  }, [searchParams]);

  // Dopo login, se c'è un redirect memorizzato in sessionStorage,
  // ci torniamo invece che alla dashboard di default.
  const goPostLogin = () => {
    let target = "/dashboard";
    try {
      const saved = sessionStorage.getItem("post_login_redirect");
      if (saved && /^\/[^/]/.test(saved)) {
        target = saved;
        sessionStorage.removeItem("post_login_redirect");
      }
    } catch { /* no-op */ }
    nav(target);
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await loginJwt(email, password);
      toast.success("Bentornato");
      goPostLogin();
    } catch (err) {
      toast.error(apiError(err, "Credenziali non valide"));
    } finally { setLoading(false); }
  };

  // iter v31 r19 — Magic link submit: l'utente inserisce email, riceve
  // link di accesso. Soluzione anti-CSRF per iOS Safari/Chrome dove
  // Google OAuth web fallisce sistematicamente per via di ITP.
  const onMagicSubmit = async (e) => {
    e.preventDefault();
    if (!magicEmail.trim() || !magicEmail.includes("@")) {
      toast.error("Inserisci la tua email");
      return;
    }
    setMagicLoading(true);
    try {
      await api.post("/auth/magic-link/request", { email: magicEmail.trim() });
      setMagicSent(true);
    } catch (err) {
      // Risposta backend è sempre 200 (anti-enumeration). Errore qui = rete.
      toast.error(apiError(err, "Errore di rete. Riprova fra qualche secondo."));
    } finally {
      setMagicLoading(false);
    }
  };

  const onGoogle = async () => {
    // iter v29 r203 — Log remoto: ci dice che il TAP è arrivato al React.
    // Se vediamo "click" sul backend ma poi nient'altro, il bug è nel plugin.
    // Se NON vediamo nemmeno il "click", il problema è prima (overlay/JS).
    if (isNative) {
      remoteLog("ios-google-login", "ui.click");
      toast.info("Apertura accesso Google…", { duration: 1500 });
    }
    setGoogleLoading(true);

    // ─── iOS NATIVO · iter v29 r200 ─────────────────────────────────
    // Niente più auth.emergentagent.com visibile all'utente: usiamo il
    // foglio nativo Google iOS via @capgo/capacitor-social-login.
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    if (isNative) {
      try {
        const result = await nativeGoogleSignIn();
        if (!result || !result.idToken) {
          setGoogleLoading(false);
          // iter v29 r201 — Diagnostica temporanea: se result è null senza
          // idToken, l'utente ha annullato O il plugin non è inizializzato.
          // Mostriamo un hint discreto invece di nessun feedback.
          toast.error("Accesso Google interrotto. Riprova.", { duration: 3000 });
          return;
        }
        // Manda l'idToken al backend per la verifica + creazione sessione.
        // iter v29 r205 — Bug fix "Load failed" su iOS native:
        // process.env.REACT_APP_BACKEND_URL è hardcoded a build-time a
        // desktop-suzuka.emergent.host, mentre l'app Capacitor è servita
        // da prontoimmo.it. Quel cross-origin + ingress che riscrive
        // Allow-Origin a "*" rompe WebKit con credentials. Stesso fix
        // applicato dentro lib/api.js: usiamo window.location.origin
        // così la fetch è same-origin e niente CORS.
        //
        // iter v33 r20 — Rimosso il fallback `|| process.env.REACT_APP_BACKEND_URL`
        // che il deployment agent segnalava come blocker prod. In browser
        // React `window` esiste sempre, quindi il fallback era dead code
        // che tornava un dominio hardcoded al build.
        const resp = await fetch(`${window.location.origin}/api/auth/google-native`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({ id_token: result.idToken }),
        });
        if (!resp.ok) {
          const detail = (await resp.json().catch(() => ({}))).detail || "Login Google non riuscito";
          toast.error(detail);
          setGoogleLoading(false);
          return;
        }
        const data = await resp.json();
        // iter v32 r15 — Bug fix: il primo login Google rimbalzava a /login.
        // Causa: salvavamo il token sotto la chiave `session_token`, ma
        // tokenStorage / axios interceptor leggono sotto `vista_jwt`. Al
        // primo /auth/me l'interceptor non trovava il Bearer → 401 → logout
        // spurio. La 2ª volta entrava perché il cookie httpOnly era ormai
        // arrivato. Fix: usa saveJwt() che scrive in TUTTI gli storage
        // (memoria + cookie + localStorage chiave corretta + Capacitor
        // Preferences UserDefaults) in modo coerente.
        saveJwt(data.token);
        if (data.user) setUser(data.user);
        toast.success(`Bentornato${data.user?.name ? `, ${data.user.name.split(" ")[0]}` : ""}`);
        goPostLogin();
      } catch (err) {
        // iter v29 r201 — Mostra il messaggio reale così sappiamo se è
        // "plugin not installed", "client id mismatch" o errore di rete.
        const msg = err?.message || err?.error || String(err) || "Login Google non riuscito";
        remoteLog("ios-google-login", "ui.exception", {
          message: msg,
          stack: err?.stack,
        });
        toast.error(`Google: ${msg}`, { duration: 6000 });
        setGoogleLoading(false);
      }
      return;
    }

    // ─── WEB BROWSER · flow Emergent tradizionale ───────────────────
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/dashboard";
    const authUrl = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
    setTimeout(() => {
      window.location.href = authUrl;
    }, 900);
  };

  return (
    <div
      className="min-h-screen bg-[#FFFFFF] grid lg:grid-cols-2"
      style={isNative ? {
        // iter v29 r196 — Safe area iOS: padding-top per non finire sotto
        // lo status bar / Dynamic Island sull'iPhone (47-59px).
        paddingTop: "env(safe-area-inset-top)",
        paddingBottom: "env(safe-area-inset-bottom)",
      } : undefined}
    >
      <GoogleHandoff visible={googleLoading} />
      {/* Brand panel — left */}
      <aside className="hidden lg:flex relative bg-[#7C8B6E] text-[#FFFFFF] flex-col justify-between p-12 overflow-hidden">
        <div className="absolute inset-0 opacity-[0.05] pointer-events-none" style={{
          backgroundImage:
            "radial-gradient(circle at 20% 20%, #FFFFFF 0%, transparent 35%), radial-gradient(circle at 80% 70%, #7C8B6E 0%, transparent 40%)",
        }} />
        <Link to="/" className="relative z-10 inline-flex" data-testid="brand-link-login-aside">
          <ProntoLogo size={32} tone="light" />
        </Link>
        <div className="relative z-10">
          <p className="font-editorial text-3xl lg:text-4xl leading-[1.1] tracking-tight">
            «Le foto decidono se un cliente ti chiama,<br/>
            <span className="italic-accent text-[#F1F4ED]">o continua a scorrere.»</span>
          </p>
          <div className="mt-10 flex items-center gap-3 text-sm opacity-80">
            <span className="w-10 h-px bg-[#F1F4ED]" />
            Una bella foto vende.
          </div>
        </div>
        <div className="relative z-10 text-xs opacity-60 tracking-widest uppercase">
          © {new Date().getFullYear()} Pronto · Pensato in Italia
        </div>
      </aside>

      {/* Form panel — right */}
      <div className="flex flex-col">
        <header className="px-6 lg:px-10 pt-6 pb-2 lg:hidden">
          {/* iter v29 r195 — Su native iOS l'app si apre già su /login,
              quindi mostriamo SOLO il mark centrato (no scritta), che dà
              respiro e modernità. Su web (mobile) mostriamo il logo full
              con tagline a sinistra (linking alla home).
              iter v29 r244 — User M1399: «non voglio il logo con la
              scritta "PRONTO" sotto, preferivo la P verde».
              iter v29 r245 — User M1400: «P verde centrata, tutto
              centrato come nell'app iOS». Centrato anche su web. */}
          {isNative ? (
            <div className="flex justify-center" data-testid="brand-mark-login-native">
              <img
                src="/logo/mark-symbol.png?v=8"
                alt="Pronto"
                className="h-14 w-14 select-none"
                draggable="false"
              />
            </div>
          ) : (
            <div className="flex justify-center" data-testid="brand-mark-login-web">
              <Link to="/" className="inline-flex" data-testid="brand-link-login">
                <img src="/logo/mark-symbol.png?v=8" alt="Pronto" className="h-14 w-14 select-none" draggable="false" />
              </Link>
            </div>
          )}
        </header>
        <main className="flex-1 flex items-center justify-center px-6 py-8">
          <div className="w-full max-w-sm">
            <h1
              className={`font-editorial leading-[1.05] tracking-tight ${isNative ? "text-5xl text-center" : "text-4xl lg:text-5xl"}`}
              data-testid="login-title"
            >
              Bentornato.
            </h1>
            <p className={`mt-3 text-[#6B6A65] ${isNative ? "text-center text-[15px]" : ""}`}>
              Accedi al tuo account.
            </p>

            <button
              onClick={onGoogle}
              className="mt-10 w-full flex items-center justify-center gap-3 border border-[#E5E7E0] bg-white px-4 py-3.5 rounded-sm hover:border-[#7C8B6E] transition-colors"
              data-testid="google-login-btn"
            >
              <GoogleLogo size={20} weight="bold" className="text-[#7C8B6E]" />
              <span className="font-medium">Continua con Google</span>
            </button>

            {/* iter v32 r12 — Rimosso il banner iOS Safari che avvisava di
                un possibile errore Google: era invasivo e creava ansia su
                un problema che spesso non si verifica. Il link "Accedi
                senza password" qui sotto resta disponibile come fallback
                per chi davvero ha problemi col flow OAuth. */}

            {/* iter 2026-06-12 — Apple Guideline 4.8: Sign in with Apple
                obbligatorio nel login se l'app offre altri login (Google).
                Riattivato dopo respinta. */}
            <div className="mt-3">
              <AppleSignInButton
                onSetUser={setUser}
                onAuthenticated={goPostLogin}
              />
            </div>

            <div className="my-6 flex items-center gap-3 text-xs text-[#6B6A65] uppercase tracking-[0.2em]">
              <span className="flex-1 h-px bg-[#E5E7E0]" />
              oppure
              <span className="flex-1 h-px bg-[#E5E7E0]" />
            </div>

            {/* iter v31 r19 — Magic-link form. Sostituisce la form password
                quando l'utente clicca "accedi senza password". Soluzione
                principale per chi usa Google OAuth e non ha password locale
                (la maggioranza degli account creati via Google). */}
            {magicMode ? (
              magicSent ? (
                <div
                  className="text-center py-2"
                  data-testid="magic-sent-panel"
                >
                  <div className="w-14 h-14 mx-auto rounded-full bg-[#F1F4ED] flex items-center justify-center mb-3">
                    <EnvelopeSimple size={26} weight="regular" className="text-[#7C8B6E]" />
                  </div>
                  <h2 className="font-editorial text-2xl tracking-tight text-[#1C1C1A]">
                    Email inviata.
                  </h2>
                  <p className="mt-2 text-[13px] text-[#6B6A65] leading-relaxed">
                    Se <strong className="text-[#1C1C1A]">{magicEmail}</strong> è
                    registrata su Pronto, ti arriva un link cliccabile entro
                    pochi minuti. Apri l'email sul tuo iPhone e tocca il bottone.
                  </p>
                  <p className="mt-2 text-[11px] text-[#9A968B]">
                    Il link vale 15 minuti e si autodistrugge dopo l'uso.
                  </p>
                  <button
                    type="button"
                    onClick={() => { setMagicMode(false); setMagicSent(false); setMagicEmail(""); }}
                    className="mt-5 text-[12.5px] text-[#6B6A65] underline hover:text-[#1C1C1A]"
                    data-testid="magic-back-to-login"
                  >
                    Torna al login con password
                  </button>
                </div>
              ) : (
                <form onSubmit={onMagicSubmit} className="space-y-3" data-testid="magic-form">
                  <div className="flex items-center gap-2 mb-1">
                    <MagicWand size={16} weight="duotone" className="text-[#7C8B6E]" />
                    <span className="text-[11px] uppercase tracking-[0.18em] font-semibold text-[#7C8B6E]">
                      Accesso senza password
                    </span>
                  </div>
                  <p className="text-[12px] text-[#6B6A65] leading-snug">
                    Inserisci la tua email. Ti arriva un link cliccabile che ti
                    fa entrare subito (anche se ti sei registrato con Google).
                  </p>
                  <input
                    type="email"
                    required
                    autoFocus
                    value={magicEmail}
                    onChange={(e) => setMagicEmail(e.target.value)}
                    data-vista
                    data-testid="magic-email-input"
                    placeholder="tua@email.it"
                  />
                  <button
                    type="submit"
                    disabled={magicLoading || !magicEmail.trim()}
                    className="btn-primary w-full justify-center disabled:opacity-60"
                    data-testid="magic-submit-btn"
                  >
                    {magicLoading ? "Invio…" : <>Mandami il link <ArrowRight size={16} weight="bold" /></>}
                  </button>
                  <button
                    type="button"
                    onClick={() => setMagicMode(false)}
                    className="block w-full text-center text-[12px] text-[#9A968B] underline hover:text-[#1C1C1A] mt-1"
                    data-testid="magic-cancel"
                  >
                    Torna al login con password
                  </button>
                </form>
              )
            ) : (
              <form onSubmit={onSubmit} className="space-y-3">
                <input
                  type="email" required value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  data-vista data-testid="login-email-input"
                  placeholder="Email"
                />
                <div className="relative">
                  <input
                    type={showPassword ? "text" : "password"} required value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    data-vista data-testid="login-password-input"
                    placeholder="Password"
                    className="pr-10 w-full"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword((s) => !s)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-[#6B6A65] hover:text-[#1C1C1A] transition-colors"
                    data-testid="login-toggle-password"
                    aria-label={showPassword ? "Nascondi password" : "Mostra password"}
                    tabIndex={-1}
                  >
                    {showPassword ? <EyeSlash size={18} weight="regular" /> : <Eye size={18} weight="regular" />}
                  </button>
                </div>
                <button type="submit" disabled={loading} className="btn-primary w-full justify-center disabled:opacity-60" data-testid="login-submit-btn">
                  {loading ? "Accesso..." : <>Accedi <ArrowRight size={16} weight="bold" /></>}
                </button>
              </form>
            )}

            {!magicMode && (
              <div className="mt-4 flex items-center justify-center gap-3 text-sm">
                <Link
                  to="/forgot-password"
                  className="text-[#6B6A65] hover:text-[#1C1C1A] underline"
                  data-testid="forgot-password-link"
                >
                  Password dimenticata?
                </Link>
                <span className="text-[#D8D6CF]">·</span>
                <button
                  type="button"
                  onClick={() => setMagicMode(true)}
                  className="text-[#7C8B6E] hover:text-[#1C1C1A] underline font-medium"
                  data-testid="open-magic-link"
                >
                  Accedi senza password
                </button>
              </div>
            )}

            {/* iter v31 r3 — Registrazione diretta in-app abilitata anche su iOS.
                I due box "Già registrato sul sito?" + "Nuovo su Pronto?" sono
                stati rimossi: erano ridondanti con i bottoni Apple/Google e
                con "Password dimenticata?" (magic link). */}
            <p className="mt-6 text-sm text-[#6B6A65] text-center">
              Non hai un account?{" "}
              <Link to="/register" className="text-[#7C8B6E] font-medium underline" data-testid="to-register-link">
                Registrati gratuitamente
              </Link>
            </p>

            <div className="mt-8 pt-5 border-t border-[#E5E7E0] flex items-center justify-center gap-1.5 text-[10.5px] text-[#9A968B]" data-testid="login-trust">
              <ShieldCheck size={12} weight="duotone" className="text-[#7C8B6E]" />
              <span>Connessione cifrata · Dati in Europa · GDPR</span>
            </div>
          </div>
        </main>
      </div>
    </div>
  );
}
